import { ContactPayload, PAGES } from "@dune/types";
import { Button, Loader } from "@dune/ui";
import {
	hasAccessToPlatform,
	roleToAccountTypeText,
	subRolesToText,
} from "@dune/vars";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { Edit02 } from "@untitled-ui/icons-react";
import { Eye, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { attachToken } from "../../api/axios";
import { impersonateUser } from "../../api/mutations/auth.mutations";
import { usersQueryKeys } from "../../api/query-keys/users.query-keys";
import { InvitationStatusChip } from "../../component/common/InvitationStatusChip";
import { useAuthStore } from "../../stores/auth.store";

export const listContactsColumns = (
	onClickEdit: (contactId: number) => void,
	onClickDelete: (contactId: number) => void,
): ColumnDef<ContactPayload>[] => [
	{
		accessorKey: "lastname",
		header: "Nom",
		cell: ({ row }) => row.original.lastname,
	},
	{
		accessorKey: "firstname",
		header: "Prénom",
		cell: ({ row }) => row.original.firstname,
	},
	{
		accessorKey: "society.denomination",
		header: "Raison Sociale",
		cell: ({ row }) => row.original.society?.denomination ?? "",
	},
	{
		accessorKey: "role",
		header: "Type",
		cell: ({ row }) => roleToAccountTypeText[row.original.role],
	},
	{
		accessorKey: "subrole",
		header: "Rôle",
		cell: ({ row }) =>
			row.original.subrole ? subRolesToText[row.original.subrole] : "",
	},
	{
		accessorKey: "accountCreationInvitationSentAt",
		header: "Statut invitation",
		cell: ({ row }) => (
			<InvitationStatusChip
				sentAt={row.original.accountCreationInvitationSentAt}
				isAccountActivated={row.original.hasActivatedAccount}
				isSuspended={!row.original.isActive}
			/>
		),
	},
	{
		accessorKey: "actions",
		header: "",
		cell: ({ row }) => {
			const setImpersonationToken = useAuthStore(
				(state) => state.setImpersonationToken,
			);
			const navigate = useNavigate();
			const queryClient = useQueryClient();

			const { mutate: impersonateUserMutate, isPending: isImpersonating } =
				useMutation({
					mutationFn: impersonateUser,
					onSuccess: ({ impersonationToken }) => {
						setImpersonationToken(impersonationToken);
						attachToken(impersonationToken);
						queryClient.refetchQueries({
							queryKey: usersQueryKeys.me(),
						});
						navigate(PAGES.ROOT);
					},
				});

			return (
				<>
					<Button
						variant="none"
						onClick={() => impersonateUserMutate(row.original.id)}
						disabled={!hasAccessToPlatform(row.original)}
					>
						{isImpersonating ? <Loader /> : <Eye />}
					</Button>

					<Button variant="none" onClick={() => onClickEdit(row.original.id)}>
						<Edit02 />
					</Button>
					<Button variant="none" onClick={() => onClickDelete(row.original.id)}>
						<Trash2 />
					</Button>
				</>
			);
		},
	},
];
